import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./editor.css";
import EditorHeader from "./editor/EditorHeader";
import EditorMainView from "./editor/EditorMainView";
import EditorSidebar from "./editor/EditorSidebar";
import {
  getExperimentState,
  saveGoalSelector,
  saveJson,
  savePageUrl,
} from "./editor/editorApi";
import { loadPage } from "./editor/iframeUtils";

const MAX_VARIATION_CHANGES = 3;
const isDisallowedRootSelector = (value) => {
  const s = value.trim().toLowerCase();
  return (
    s === "html" ||
    s === "body" ||
    /^html:nth-of-type\(\d+\)$/.test(s) ||
    /^body:nth-of-type\(\d+\)$/.test(s)
  );
};

export default function Editor() {
  const { experimentId } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const iframeControlRef = useRef(null);
  const selectedElRef = useRef(null);
  const variationJsonRef = useRef([]);
  const selectorRef = useRef("");
  const contentRef = useRef("");
  const textColorRef = useRef("#000000");
  const bgColorRef = useRef("#ffffff");
  const isDirtyRef = useRef(false);

  const [step, setStep] = useState(1);
  const [pageUrl, setPageUrl] = useState("");
  const [originalUrl, setOriginalUrl] = useState("");
  const [variationJson, setVariationJson] = useState([]);
  const [goalSelector, setGoalSelector] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSetupComplete, setIsSetupComplete] = useState(false);

  const [selector, setSelector] = useState("");
  const [content, setContent] = useState("");
  const [textColor, setTextColor] = useState("#000000");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [isSavingVariation, setIsSavingVariation] = useState(false);
  const [previewMode, setPreviewMode] = useState("variation");

  useEffect(() => {
    const loadExperimentState = async () => {
      try {
        const data = await getExperimentState(experimentId);
        let parsedJson = [];
        try {
          if (data.json) {
            parsedJson = typeof data.json === "string" ? JSON.parse(data.json) : data.json;
          }
        } catch (e) {
          console.error("JSON Parse Error:", e);
        }

        setStep(data.step);
        setPageUrl(data.pageUrl || "");
        setOriginalUrl(data.pageUrl || "");
        const initialChanges = Array.isArray(parsedJson) ? parsedJson : [];
        setVariationJson(initialChanges);
        variationJsonRef.current = initialChanges;
        setGoalSelector(data.goalSelector || "");
        setIsSetupComplete(data.step === 3);
      } catch (err) {
        console.error("Failed to load editor state:", err);
        alert(err.message || "Unable to load editor state.");
      } finally {
        setLoading(false);
      }
    };

    loadExperimentState();
  }, [experimentId]);

  useEffect(() => {
    variationJsonRef.current = variationJson;
  }, [variationJson]);
  useEffect(() => {
    selectorRef.current = selector;
  }, [selector]);
  useEffect(() => {
    contentRef.current = content;
  }, [content]);
  useEffect(() => {
    textColorRef.current = textColor;
  }, [textColor]);
  useEffect(() => {
    bgColorRef.current = bgColor;
  }, [bgColor]);

  const upsertChange = (changes, nextChange) => {
    const base = changes.filter((v) => v.selector !== nextChange.selector);
    return [...base, nextChange];
  };

  const stageCurrentSelectionIfDirty = () => {
    const currentSelector = selectorRef.current.trim();
    if (!isDirtyRef.current || !currentSelector || isDisallowedRootSelector(currentSelector)) return;

    const staged = upsertChange(variationJsonRef.current || [], {
      selector: currentSelector,
      content: contentRef.current,
      styles: { color: textColorRef.current, backgroundColor: bgColorRef.current },
    });
    variationJsonRef.current = staged;
    setVariationJson(staged);
    isDirtyRef.current = false;
  };

  const handleLoadPage = async (
    iframe,
    allowSelection,
    shouldApply = false,
    forceRefresh = false
  ) => {
    await loadPage({
      iframe,
      pageUrl,
      forceRefresh,
      allowSelection,
      shouldApply,
      step,
      variationJson,
      selectedElRef,
      setSelector,
      setContent,
      setTextColor,
      setBgColor,
      setGoalSelector,
      onBeforeElementChange: stageCurrentSelectionIfDirty,
    });
  };

  const canNavigateToStep = (targetStep) => {
    if (isSetupComplete) return true;
    if (targetStep === 1) return true;
    if (targetStep === 2) return (variationJsonRef.current || []).length > 0;
    if (targetStep === 3) return (variationJsonRef.current || []).length > 0;
    return false;
  };

  const handleStepChange = (targetStep) => {
    if (!canNavigateToStep(targetStep)) return;
    setStep(targetStep);
  };

  const saveVariationAndNext = async () => {
    if (isSavingVariation) return;
    stageCurrentSelectionIfDirty();

    const normalizedSelector = selector.trim();
    if (!normalizedSelector) {
      alert("Please select an element before saving.");
      return;
    }
    if (isDisallowedRootSelector(normalizedSelector)) {
      alert("Please select a specific page element, not the full page.");
      return;
    }

    const currentChanges = Array.isArray(variationJsonRef.current) ? variationJsonRef.current : [];
    const existing = currentChanges.find((v) => v.selector === normalizedSelector);
    const baseChanges = currentChanges.filter((v) => v.selector !== normalizedSelector);

    if (!existing && baseChanges.length >= MAX_VARIATION_CHANGES) {
      alert(`You can save up to ${MAX_VARIATION_CHANGES} variation changes.`);
      return;
    }

    const updatedChanges = [
      ...baseChanges,
      { selector: normalizedSelector, content, styles: { color: textColor, backgroundColor: bgColor } },
    ];

    // Update local state first so subsequent edits always merge on latest list.
    setVariationJson(updatedChanges);
    variationJsonRef.current = updatedChanges;
    isDirtyRef.current = false;
    setIsSavingVariation(true);

    try {
      await saveJson(experimentId, updatedChanges);
      await savePageUrl(experimentId, pageUrl);
    } catch (err) {
      console.error("Failed to save variation:", err);
      alert(err.message || "Error saving variation.");
    } finally {
      setIsSavingVariation(false);
    }
  };

  const proceedToGoalSetup = () => {
    if (variationJson.length === 0) {
      alert("Please save at least one variation change before proceeding.");
      return;
    }
    setStep(2);
  };

  const saveGoalAndNext = async () => {
    if (!goalSelector.trim() || isDisallowedRootSelector(goalSelector)) {
      alert("Please select a valid goal element.");
      return;
    }
    try {
      await saveGoalSelector(experimentId, goalSelector);
      setIsSetupComplete(true);
      setStep(3);
      window.location.reload();
    } catch (err) {
      console.error("Failed to save goal:", err);
      alert(err.message || "Error saving goal selector.");
    }
  };

  useEffect(() => {
    if (loading || !pageUrl) return;
    if (step < 3) handleLoadPage(iframeRef.current, !isSetupComplete, true);
    else {
      handleLoadPage(iframeControlRef.current, false, false);
      handleLoadPage(iframeRef.current, false, true);
    }
  }, [step, pageUrl, loading, variationJson, isSetupComplete]);

  useEffect(() => {
    if (!isSetupComplete && selectedElRef.current && step === 1) {
      selectedElRef.current.innerText = content;
      selectedElRef.current.style.setProperty("color", textColor, "important");
      selectedElRef.current.style.setProperty("background-color", bgColor, "important");
    }
  }, [content, textColor, bgColor, step, isSetupComplete]);

  if (loading) return <div>Loading...</div>;

  const isStepOneLayout = step === 1 && !isSetupComplete;
  const showBottomDock = true;

  return (
    <div className={`editor-root ${isStepOneLayout ? "editor-root-step-one" : ""}`}>
      {!showBottomDock && !isStepOneLayout && (
        <EditorHeader
          step={step}
          onBack={() => navigate("/viewexperiment")}
          onStepChange={handleStepChange}
          isStepEnabled={canNavigateToStep}
        />
      )}

      <div className={`editor-body ${isStepOneLayout ? "editor-body-step-one" : ""}`}>
        <EditorMainView
          step={step}
          iframeRef={iframeRef}
          iframeControlRef={iframeControlRef}
          previewMode={previewMode}
        />

        {!showBottomDock && (
          <EditorSidebar
            step={step}
            pageUrl={pageUrl}
            setPageUrl={setPageUrl}
            onReload={() => handleLoadPage(iframeRef.current, !isSetupComplete, true, true)}
            readOnly={isSetupComplete}
            content={content}
            setContent={(value) => {
              isDirtyRef.current = true;
              setContent(value);
            }}
            textColor={textColor}
            setTextColor={(value) => {
              isDirtyRef.current = true;
              setTextColor(value);
            }}
            bgColor={bgColor}
            setBgColor={(value) => {
              isDirtyRef.current = true;
              setBgColor(value);
            }}
            onSaveVariation={saveVariationAndNext}
            isSavingVariation={isSavingVariation}
            variationCount={variationJson.length}
            onProceedToGoal={proceedToGoalSetup}
            goalSelector={goalSelector}
            onSaveGoal={saveGoalAndNext}
            previewMode={previewMode}
            onPreviewModeChange={setPreviewMode}
          />
        )}
      </div>

      {showBottomDock && (
        <div className="editor-bottom-dock">
          <EditorSidebar
            compact
            step={step}
            pageUrl={pageUrl}
            setPageUrl={setPageUrl}
            onReload={() => handleLoadPage(iframeRef.current, !isSetupComplete, true, true)}
            readOnly={isSetupComplete}
            content={content}
            setContent={(value) => {
              isDirtyRef.current = true;
              setContent(value);
            }}
            textColor={textColor}
            setTextColor={(value) => {
              isDirtyRef.current = true;
              setTextColor(value);
            }}
            bgColor={bgColor}
            setBgColor={(value) => {
              isDirtyRef.current = true;
              setBgColor(value);
            }}
            onSaveVariation={saveVariationAndNext}
            isSavingVariation={isSavingVariation}
            variationCount={variationJson.length}
            onProceedToGoal={proceedToGoalSetup}
            goalSelector={goalSelector}
            onSaveGoal={saveGoalAndNext}
            previewMode={previewMode}
            onPreviewModeChange={setPreviewMode}
          />
          <EditorHeader
            compact
            step={step}
            onBack={() => navigate("/viewexperiment")}
            onStepChange={handleStepChange}
            isStepEnabled={canNavigateToStep}
          />
        </div>
      )}
    </div>
  );
}
